/**
 * Pickup Availability JavaScript
 * Handles pickup availability display updates and modal/drawer functionality
 */

class PickupAvailability {
  constructor(container) {
    this.container = container;
    this.variantId = container.dataset.variantId;
    
    // Find drawer - check next sibling first, then search by data attribute
    let drawer = container.nextElementSibling;
    if (drawer && drawer.classList.contains('pickup-availability-drawer')) {
      this.drawer = drawer;
    } else {
      // Fallback: search by data attribute
      this.drawer = document.querySelector(
        `.pickup-availability-drawer[data-variant-id="${this.variantId}"]`
      );
    }
    
    this.openButton = container.querySelector(
      '[data-pickup-availability-modal-open]'
    );
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupDrawer();
  }

  bindEvents() {
    // Handle modal/drawer open button
    if (this.openButton) {
      this.openButton.addEventListener('click', () => this.openDrawer());
    }

    // Listen for variant changes from variant picker
    this.container.addEventListener('variant:changed', (e) => {
      this.handleVariantChange(e.detail);
    });

    // Also listen on document for variant changes
    document.addEventListener('variant:changed', (e) => {
      // Only handle if it's for our section
      if (!e.detail.sectionId || e.detail.sectionId === this.container.closest('[data-section-id]')?.dataset.sectionId) {
        this.handleVariantChange(e.detail);
      }
    });

    // Close drawer on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer && this.drawer.classList.contains('pickup-availability-drawer--open')) {
        this.closeDrawer();
      }
    });
  }

  setupDrawer() {
    if (!this.drawer) return;

    // Bind close button
    const closeButton = this.drawer.querySelector('[data-pickup-availability-modal-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeDrawer());
    }

    // Close drawer when clicking overlay
    const overlay = this.drawer.querySelector('.pickup-availability-drawer__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.closeDrawer());
    }
  }

  openDrawer() {
    if (!this.drawer) return;

    this.drawer.classList.add('pickup-availability-drawer--open');
    this.drawer.setAttribute('aria-hidden', 'false');
    
    if (this.openButton) {
      this.openButton.setAttribute('aria-expanded', 'true');
    }

    // Focus trap - focus the close button
    const closeButton = this.drawer.querySelector('[data-pickup-availability-modal-close]');
    if (closeButton) {
      closeButton.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.drawer.classList.remove('pickup-availability-drawer--open');
    this.drawer.setAttribute('aria-hidden', 'true');

    if (this.openButton) {
      this.openButton.setAttribute('aria-expanded', 'false');
      this.openButton.focus();
    }

    // Restore body scroll
    document.body.style.overflow = '';
  }

  async handleVariantChange(detail) {
    // Get the new variant ID from detail or URL
    const variantId = detail?.variantId || new URLSearchParams(window.location.search).get('variant');

    if (!variantId || variantId === this.variantId) {
      return;
    }

    // Update pickup availability for the new variant
    try {
      await this.updatePickupAvailability(variantId);
    } catch (error) {
      console.warn('Could not update pickup availability dynamically:', error);
      // The pickup availability will update on next page load
    }
  }

  async updatePickupAvailability(variantId) {
    if (variantId === this.variantId) {
      return; // No change needed
    }

    // For now, we'll rely on the page updating via URL change
    // In a full implementation, you could fetch the updated snippet via AJAX
    // This is a simplified version that works with Shopify's standard variant selection
    
    // Update variant ID for tracking
    this.variantId = variantId;
    this.container.dataset.variantId = variantId;
    
    // Note: Full pickup availability update would require fetching the updated snippet
    // via Shopify's section rendering API, which is more complex and may require
    // server-side support. For now, the pickup availability will update when the
    // variant changes via the variant picker's AJAX update mechanism.
  }
}

// Initialize pickup availability components
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('[data-pickup-availability-container]');
  containers.forEach((container) => {
    // Store instance on container for easy access
    container.pickupAvailabilityInstance = new PickupAvailability(container);
  });
});

// Export for use in other scripts
window.PickupAvailability = PickupAvailability;

